import { usePrevious } from "@fidget/common/hooks/usePrevious";
import classNames from "classnames";
import { debounce } from "lodash";
import { observer } from "mobx-react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import styles from "./chatComponent.css";
interface Props<TChatItem> {
  loader?: React.ReactNode;
  dynamicSuggestions?: React.ReactNode;
  scrollButton?: React.ReactNode;
  notification?: React.ReactNode;
  typingUsersComponent?: React.ReactNode;

  items: ReadonlyArray<TChatItem>;
  renderItem(item: TChatItem): React.ReactNode;

  onScrolledToTop: () => void;
  onIsScrolledToBottom: (scrolledToBottom: boolean) => void;
  isTyping?: boolean;

  isScrolledToBottom?: boolean;
  containerClassName?: string;
  messagesContainerClassName?: string;
}

export const ChatComponent = observer(
  forwardRef<ChatComponentRef, Props<unknown>>(function ChatComponentType(props, ref) {
    const {
      loader,
      scrollButton,
      notification,
      typingUsersComponent,
      dynamicSuggestions,
      items,
      renderItem,
      onScrolledToTop,
      onIsScrolledToBottom,
      isTyping,
      isScrolledToBottom,
      containerClassName,
      messagesContainerClassName
    } = props;

    const scrollContainer = useRef<HTMLDivElement>();
    const scrollBottomAnchor = useRef<HTMLDivElement>();
    const [isShowingScrollButton, setIsShowingScrollButton] = useState(false);
    const isScrollMarginReached = useRef<boolean>(false);
    const prevItemsLength = usePrevious<number>(items.length);
    const prevScrollData = scrollContainer.current && getScrollData(scrollContainer.current);

    useImperativeHandle(ref, () => ({
      scrollToBottom: scrollContainerToBottom
    }));

    const handleScroll = useCallback(
      debounce(
        (container: HTMLDivElement) => {
          const { scrollHeight, scrollTop, scrollBottom, offsetHeight } = getScrollData(container);

          // detect if user reached top by scrolling while preventing multiple Props.onScrolledToTop calls
          const scrolledToTop = scrollTop <= TOP_SCROLL_THRESHOLD;
          if (scrolledToTop && !isScrollMarginReached.current) {
            onScrolledToTop();
          }
          isScrollMarginReached.current = scrolledToTop;

          const showScrollButton = scrollHeight - scrollTop >= 2 * offsetHeight;
          setIsShowingScrollButton(showScrollButton);

          onIsScrolledToBottom(scrollBottom <= BOTTOM_SCROLL_THRESHOLD);
        },
        200,
        { leading: true }
      ),
      [onIsScrolledToBottom, onScrolledToTop]
    );

    useEffect(() => {
      scrollContainerToBottom();

      return () => {
        handleScroll.cancel();
      };
    }, []);

    /**
     * Passes event's target to debounced handleScroll to prevent its nullufication.
     * XXX: this will also be triggered by setting scrollTop / calling scrollIntoView
     */
    const onScroll = (ev: React.UIEvent) => handleScroll(ev.target as HTMLDivElement);

    const shouldScrollToBottom = (): boolean => {
      if (!(scrollContainer.current && prevScrollData)) {
        return false;
      }
      const newScroll = getScrollData(scrollContainer.current);

      // if scroll height changed → assume new elements added or removed (including typing indicator, spinner)
      // OR if offset height changed → chat items container resized
      return (
        (prevScrollData.scrollBottom <= BOTTOM_SCROLL_THRESHOLD || !!isScrolledToBottom) &&
        (newScroll.scrollHeight !== prevScrollData.scrollHeight ||
          newScroll.offsetHeight !== prevScrollData.offsetHeight)
      );
    };

    useLayoutEffect(() => {
      if (shouldScrollToBottom()) {
        // if container had been scrolled to bottom → keep it that way
        // do not smooth scroll if setting up → prevents handleScroll + scrolledToTop
        return scrollContainerToBottom();
      }

      const historyPageHasLoadedWhileScrolledToTop =
        scrollContainer.current &&
        prevItemsLength &&
        prevItemsLength < items.length &&
        scrollContainer.current.scrollTop < TOP_SCROLL_THRESHOLD;

      if (historyPageHasLoadedWhileScrolledToTop && prevScrollData) {
        const scrollTopBeforeAddingNewPage =
          scrollContainer.current!.scrollHeight - prevScrollData.scrollHeight;
        scrollContainer.current!.scrollTop = scrollTopBeforeAddingNewPage;
      }
    }, [items, isTyping]);

    /**
     * NOTE: using smooth scrollIntoView will delay scroll pos updates and cause
     *       issues on quick consecutive updates of the component
     */
    const scrollContainerToBottom = ({ smooth = false }: ScrollToBottomParams = {}) => {
      if (smooth && scrollBottomAnchor.current) {
        scrollBottomAnchor.current.scrollIntoView({ behavior: "smooth", block: "end" });
      } else if (scrollContainer.current) {
        requestAnimationFrame(
          () => (scrollContainer.current!.scrollTop = scrollContainer.current!.scrollHeight)
        );
      }
    };

    const renderAnchor = () => (
      <div
        ref={(el: HTMLDivElement) => el && (scrollBottomAnchor.current = el)}
        className={styles.anchor}
      />
    );

    const render = () => {
      return (
        <div className={classNames(styles.chat, containerClassName)}>
          {notification}
          <div
            className={classNames(styles.messages, messagesContainerClassName)}
            ref={(el) => el && (scrollContainer.current = el)}
            onScroll={onScroll}
          >
            {loader}
            {items.map((item) => renderItem(item))}
            {dynamicSuggestions}
            {typingUsersComponent}
            {renderAnchor()}
          </div>
          {isShowingScrollButton && scrollButton}
        </div>
      );
    };

    return render();
  })
);

/** value of bottom scroll below which chat container is considered "scrolled to bottom" */
const BOTTOM_SCROLL_THRESHOLD = 20;
/** value of top scroll below which chat container is considered "scrolled to top" */
const TOP_SCROLL_THRESHOLD = 20; // > 16 will prevent issues with typing indicators

interface ScrollData {
  scrollTop: number;
  scrollBottom: number;
  scrollHeight: number;
  offsetHeight: number;
}

const getScrollData = ({ scrollHeight, offsetHeight, scrollTop }: HTMLDivElement): ScrollData => {
  return {
    scrollHeight,
    offsetHeight,
    scrollTop,
    scrollBottom: scrollHeight - offsetHeight - scrollTop
  };
};

interface ScrollToBottomParams {
  smooth?: boolean;
}

export interface ChatComponentRef {
  scrollToBottom(params?: ScrollToBottomParams): void;
}
